import axios from "axios";

// Always call the API via relative paths (`/api/...`). This way the browser
// uses whichever host is serving the page (cheatcraft.net, the preview
// emergentagent.com URL, or localhost) and the request is always SAME-ORIGIN,
// which eliminates every CORS headache. The Emergent ingress routes `/api/*`
// to the backend pod on every environment. `REACT_APP_BACKEND_URL` is
// deliberately NOT used here — it's kept only for WebSocket URL derivation
// on dev servers where the frontend runs on a different port.
export const API_BASE = "/api";

const TOKEN_KEY = "cc_access_token";

export const setAuthToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};
export const getAuthToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
};

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  // Cookies still work on same-origin (preview), but cross-origin (custom
  // domain → emergent.host) the ingress responds with `Allow-Origin: *`,
  // which the spec forbids combining with credentials. We instead pass the
  // JWT via the `Authorization: Bearer` header — backend supports both.
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const tok = getAuthToken();
  if (tok) config.headers.Authorization = `Bearer ${tok}`;
  // Admin endpoints accept the password via `X-Admin-Token` in addition to
  // the legacy HttpOnly cookie — needed when the frontend runs cross-origin.
  const url = (config.url || "").toString();
  if (url.includes("/admin/") || url.startsWith("admin/") || url.startsWith("/admin")) {
    try {
      const adminTok = localStorage.getItem("cc_admin_token");
      if (adminTok) config.headers["X-Admin-Token"] = adminTok;
    } catch { /* ignore */ }
  }
  return config;
});

/** Convert FastAPI error payloads (string or array of {msg}) to a safe string. */
export function formatApiError(err, fallback = "Something went wrong.") {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ") || fallback;
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ─── Auth ────────────────────────────────────────────────────────────
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authLogin = (payload) =>
  api.post("/auth/login", payload).then((r) => {
    if (r.data?.access_token) setAuthToken(r.data.access_token);
    return r.data;
  });
export const authRegister = (payload) =>
  api.post("/auth/register", payload).then((r) => {
    if (r.data?.access_token) setAuthToken(r.data.access_token);
    return r.data;
  });
export const authLogout = () =>
  api.post("/auth/logout").finally(() => setAuthToken(null)).then((r) => r.data);
export const authForgot = (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const authReset = (token, new_password) =>
  api.post("/auth/reset-password", { token, new_password }).then((r) => r.data);
export const authVerify = (token) =>
  api.post("/auth/verify-email", { token }).then((r) => r.data);
export const authResendVerify = (email) =>
  api.post("/auth/resend-verification", { email }).then((r) => r.data);

export const deleteMyAccount = () => api.delete("/auth/me").then((r) => r.data);

export const uploadAvatar = (file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post("/uploads/avatar", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    })
    .then((r) => r.data);
};

export const updateCreatorUpsells = (id, upsells) =>
  api.patch(`/creators/${id}/upsells`, { upsells }).then((r) => r.data);

// ─── File upload ────────────────────────────────────────────────────
// New gated delivery upload. Writes to `deliveries/<order_id>/`, watermarks,
// marks the order delivered — all in one atomic call.
export const uploadDelivery = (orderId, file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post(`/orders/${orderId}/delivery-upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    })
    .then((r) => r.data);
};

// Helpers kept for places that need a pointer URL. We return relative paths
// so the browser stays same-origin on every host (cheatcraft.net, the
// preview URL, localhost…). The Emergent ingress routes `/api/*` to the
// backend on every deployment.
export const previewUrl = (orderId) => `/api/orders/${orderId}/preview`;
export const downloadUrl = (orderId) => `/api/orders/${orderId}/download`;

/** Fetch a gated file as a blob URL the browser can embed / download. */
export const fetchGatedBlob = async (orderId, kind /* "preview" | "download" */) => {
  const r = await api.get(`/orders/${orderId}/${kind}`, { responseType: "blob" });
  return URL.createObjectURL(r.data);
};

export const uploadPortfolio = (file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post("/uploads/portfolio", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    })
    .then((r) => r.data);
};

export const uploadSoftware = (file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post("/uploads/software", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    })
    .then((r) => r.data);
};

export const uploadChatAttachment = (file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post("/uploads/chat", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    })
    .then((r) => r.data);
};

export const uploadAdminPortfolio = (file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post("/uploads/admin-portfolio", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    })
    .then((r) => r.data);
};

export const updateOrderSoftware = (orderId, payload) =>
  api.post(`/orders/${orderId}/software`, payload).then((r) => r.data);

// ─── Crypto / NOWPayments ───────────────────────────────────────────
export const getOrderPayment = (id) => api.get(`/orders/${id}/payment`).then((r) => r.data);
export const getCreatorWallet = (id) => api.get(`/creators/${id}/wallet`).then((r) => r.data);
export const updateCreatorWallet = (id, ltc_address) =>
  api.patch(`/creators/${id}/wallet`, { ltc_address }).then((r) => r.data);

// ─── SLA / refund ───────────────────────────────────────────────────
export const getOrderSla = (id) => api.get(`/orders/${id}/sla`).then((r) => r.data);
export const refundOrder = (id) => api.post(`/orders/${id}/refund`).then((r) => r.data);

// ─── Admin ──────────────────────────────────────────────────────────
const ADMIN_TOKEN_KEY = "cc_admin_token";
export const setAdminToken = (t) => { if (t) localStorage.setItem(ADMIN_TOKEN_KEY, t); else localStorage.removeItem(ADMIN_TOKEN_KEY); };
export const getAdminToken = () => { try { return localStorage.getItem(ADMIN_TOKEN_KEY) || null; } catch { return null; } };

export const adminLogin = (password) =>
  api.post("/admin/login", { password }).then((r) => {
    if (r.data?.token) setAdminToken(r.data.token);
    return r.data;
  });
export const adminLogout = () =>
  api.post("/admin/logout").finally(() => setAdminToken(null)).then((r) => r.data);
export const adminMe = () => api.get("/admin/me").then((r) => r.data);
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
export const adminListOrders = (params) => api.get("/admin/orders", { params }).then((r) => r.data);
export const adminListCreators = () => api.get("/admin/creators").then((r) => r.data);
export const adminPatchCreator = (id, payload) => api.patch(`/admin/creators/${id}`, payload).then((r) => r.data);
export const adminDeleteCreator = (id) => api.delete(`/admin/creators/${id}`).then((r) => r.data);
export const adminVerifyCreator = (id) => api.post(`/admin/creators/${id}/verify`).then((r) => r.data);
export const adminRetryPayout = (orderId) =>
  api.post(`/admin/orders/${orderId}/retry-payout`).then((r) => r.data);

// Creator LTC withdrawals (mass-payouts) admin console
export const adminListPayouts = (params) =>
  api.get("/admin/payouts", { params }).then((r) => r.data);
export const adminResyncPayout = (withdrawalId) =>
  api.post(`/admin/payouts/${withdrawalId}/resync`).then((r) => r.data);
export const adminOxapayBalance = () => api.get("/admin/oxapay/balance").then((r) => r.data);

// ─── Hire / Requests (job board) ────────────────────────────────────
export const getRequestMeta = () => api.get("/requests/meta").then((r) => r.data);
export const listRequests = (params) => api.get("/requests", { params }).then((r) => r.data);
export const getRequest = (id) => api.get(`/requests/${id}`).then((r) => r.data);
export const createRequest = (payload) => api.post("/requests", payload).then((r) => r.data);
export const applyToRequest = (id, message) =>
  api.post(`/requests/${id}/apply`, { message: message || "" }).then((r) => r.data);
export const patchRequest = (id, status) =>
  api.patch(`/requests/${id}`, { status }).then((r) => r.data);
export const myRequests = () => api.get("/my/requests").then((r) => r.data);


// ─── Notifications ──────────────────────────────────────────────────
export const listNotifications = (since) =>
  api.get("/notifications", { params: since ? { since } : {} }).then((r) => r.data);
export const markNotificationsRead = (ids) =>
  api.post("/notifications/read", ids ? { ids } : {}).then((r) => r.data);

// ─── Domain ──────────────────────────────────────────────────────────
export const listCreators = (params) => api.get("/creators", { params }).then((r) => r.data);
export const getCreator = (id) => api.get(`/creators/${id}`).then((r) => r.data);
export const getStats = () => api.get("/stats").then((r) => r.data);

export const listOrders = (params) => api.get("/orders", { params }).then((r) => r.data);
export const getOrder = (id) => api.get(`/orders/${id}`).then((r) => r.data);
export const createOrder = (payload) => api.post("/orders", payload).then((r) => r.data);
export const updateOrderStatus = (id, payload) =>
  api.patch(`/orders/${id}/status`, payload).then((r) => r.data);
export const deliverOrder = (id, deliverable_url, preview_url) =>
  api.post(`/orders/${id}/deliver`, { deliverable_url, preview_url }).then((r) => r.data);
export const approveOrder = (id) => api.post(`/orders/${id}/approve`).then((r) => r.data);
export const acceptOrder = (id) => api.post(`/orders/${id}/accept`).then((r) => r.data);
export const declineOrder = (id) => api.post(`/orders/${id}/decline`).then((r) => r.data);
export const reviseOrder = (id, feedback) =>
  api.post(`/orders/${id}/revise`, { feedback }).then((r) => r.data);

export const listConversations = () => api.get("/conversations").then((r) => r.data);
export const listMessages = (conversationId) =>
  api.get("/messages", { params: { conversation_id: conversationId } }).then((r) => r.data);
export const postMessage = (payload) => api.post("/messages", payload).then((r) => r.data);
export const startConversation = (creatorId) =>
  api.post(`/conversations/start?creator_id=${creatorId}`).then((r) => r.data);

export const markConversationRead = (conversationId) =>
  api.post(`/conversations/${conversationId}/read`).then((r) => r.data);

export const getUnreadCount = () =>
  api.get("/conversations/unread-count").then((r) => r.data);

export const getWallet = () => api.get("/wallet").then((r) => r.data);
export const withdraw = (payload) => api.post("/wallet/withdraw", payload).then((r) => r.data);
export const withdrawLtc = (payload) => api.post("/wallet/withdraw-ltc", payload).then((r) => r.data);

export const getCompanyMe = () => api.get("/companies/me").then((r) => r.data);

export const getDashboard = () => api.get("/dashboard").then((r) => r.data);

export const updateCreator = (id, payload) => api.patch(`/creators/${id}`, payload).then((r) => r.data);
export const postReview = (payload) => api.post("/reviews", payload).then((r) => r.data);
